<!--
  File name: Signup.vue
  Program Description:
    - This component contains the fields needed to be field out by the registering
      user.
    - This also contains the terms and condition that needs to be read and approved
      by the user before signing up to the system

    Includes:
    - TermsSignup
    - Functions that send the info and the request for user signup
-->
<template>
  <v-container class="layout">
    <v-container
      class="fill-height container rounded d-flex justify-center"
      fluid
      style="margin-top: 5%"
    >
      <v-col
        cols="5"
        class="padding"
      >
        <v-card
          :loading="isLoading"
          class="signupCard "
          loader-height="4"
        >
          <div
            class="
          content"
          >
            <v-row class="mb-3">
              <p class="signup">
                Sign Up
              </p>
            </v-row>
            <v-row class="mb-n6">
              <v-col
                cols="6"
              >
                <v-text-field
                  v-model.trim="firstName"
                  class="black--text"
                  label="First Name"
                  type="text"
                  dense
                  outlined
                  :disabled="$data.isLoading"
                  :error-messages="genericError('firstName', 'First name')"
                  @input="$v.firstName.$touch()"
                  @blur="$v.firstName.$touch()"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.trim="lastName"
                  class="black--text"
                  label="Last Name"
                  type="text"
                  dense
                  outlined
                  :disabled="$data.isLoading"
                  :error-messages="genericError('lastName', 'Last name')"
                  @input="$v.lastName.$touch()"
                  @blur="$v.lastName.$touch()"
                />
              </v-col>
            </v-row>
            <v-row class="mb-n6">
              <v-col cols="6">
                <v-text-field
                  v-model.trim="email"
                  class="black--text"
                  label="Official DPO Email"
                  type="email"
                  dense
                  outlined
                  :disabled="$data.isLoading"
                  :error-messages="genericError('email', 'Email')"
                  @input="$v.email.$touch()"
                  @blur="$v.email.$touch()"
                />
              </v-col>
              <v-col
                cols="6"
              >
                <v-text-field
                  v-model.trim="contactNumber"
                  class="black--text"
                  label="Contact Number"
                  type="text"
                  dense
                  outlined
                  :disabled="$data.isLoading"
                  :error-messages="genericError('contactNumber', 'Contact Number')"
                  @input="$v.contactNumber.$touch()"
                  @blur="$v.contactNumber.$touch()"
                >
                  <template
                    slot="append"
                  >
                    <v-tooltip
                      max-width="250"
                      top
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="gray"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-information
                        </v-icon>
                      </template>
                      <span>If telephone number, include country code, area code and number</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row class="mb-n6">
              <v-col cols="4">
                <VAutocomplete
                  v-model="sex"
                  label="Sex"
                  dense
                  :items="$store.state.defaults.sex"
                  outlined
                  :error-messages="genericError('sex', 'Sex')"
                  :disabled="$data.isLoading"
                  @input="$v.sex.$touch()"
                  @blur="$v.sex.$touch()"
                />
              </v-col>
              <v-col cols="8">
                <v-text-field
                  v-model.trim="organization"
                  class="black--text "
                  label="Name of Organization"
                  type="text"
                  dense
                  outlined
                  :disabled="$data.isLoading"
                  :error-messages="genericError('organization', 'Organization')"
                  @input="$v.organization.$touch()"
                  @blur="$v.organization.$touch()"
                />
              </v-col>
            </v-row>
            <v-row class="mb-n6">
              <v-col cols="6">
                <VAutocomplete
                  v-model="selectedSector"
                  label="Sector"
                  dense
                  :items="sectorValues"
                  outlined
                  :disabled="$data.isLoading"
                  :error-messages="genericError('selectedSector', 'Sector')"
                  @input="$v.selectedSector.$touch()"
                  @blur="$v.selectedSector.$touch()"
                />
              </v-col>
              <v-col cols="6">
                <VAutocomplete
                  v-model="selectedSubSector"
                  label="Sub Sector"
                  dense
                  :items="subSectorValues"
                  outlined
                  :disabled="subSector.length === 0 ? true : false"
                />
              </v-col>
            </v-row>

            <TermsSignup
              @change="emitData"
            >
              <template v-slot:action>
                <v-row class="d-flex justify-center">
                  <v-btn
                    color="primary"
                    type="button"
                    :disabled="!termsandconditions"
                    style="padding: 26px 110px"
                    @click="confirm('create')"
                  >
                    Create Account
                  </v-btn>
                </v-row>
                <v-row
                  style="padding: 0 15px;"
                  align="center"
                  justify="center"
                >
                  <v-col
                    cols="12"
                    align="center"
                    class="font-weight-medium"
                  >
                    Already have an account?
                    <router-link
                      class="nav-link"
                      to="login"
                    >
                      <span
                        class="primary--text font-weight-black"
                      >
                        Login here
                      </span>
                    </router-link>
                  </v-col>
                </v-row>
              </template>
            </TermsSignup>
          </div>
        </v-card>
      </v-col>
    </v-container>
  </v-container>
</template>
<script>
import crud from '@/utils/rest/crud'
import TermsSignup from '@/components/TermsSignup'
import users from '@/utils/webapi/users'
import adminProfileMixin from '@/mixins/adminProfileMixin'
import { validationMixin, custom, validationErrors } from '@/utils/validations'
import { uploadID } from '@/utils/helpers'
export default {
  name: 'Signup',
  components: {
    TermsSignup
  },
  mixins: [validationMixin, adminProfileMixin],
  props: {
    accept: {
      type: String,
      default: '.jpg,.jpeg,.png'
    }
  },
  data: () => ({
    termsandconditions: false,
    isLoading: false,
    organization: '',
    openDialog: false,
    sector: [],
    subSector: [],
    sectorValues: [],
    subSectorValues: [],
    selectedSector: '',
    selectedSubSector: '',
    sectorId: '',
    subSectorId: undefined,
    outerCheckbox: false
  }),
  validations () {
    return custom.signup
  },
  computed: {
    ...validationErrors,
    agencyQuery () {
      return { agencyName: { $ne: [] } }
    },
    userRoleQuery () {
      return { _configType: { $nin: ['guest', 'default'] } }
    },

    enableSignup () {
      return !this.termsandconditions
    },
    hint () {
      return `Can accept the following file types: ${this.$props.accept}`
    }
  },
  watch: {
    selectedSector: function () {
      const selectedSector = this.sector.filter(sector => sector.value === this.selectedSector)
      this.sectorId = selectedSector[0]._id
      this.getSubSectorData()
    },
    selectedSubSector: function () {
      const selectedSubSector = this.subSector.filter(sub => sub.value === this.selectedSubSector)
      this.subSectorId = selectedSubSector[0]._id
    }
  },
  created () {
    this.getSectorData()
  },
  methods: {
    // uses data prop as new value for this.termsand conditions
    emitData (data) {
      this.termsandconditions = data
    },
    // get details from sign up form and send details for creation
    // behaviour handling for success and fail
    async create () {
      this.isLoading = true
      const info = this.updateInfo
      const payload = {
        firstName: info.firstName,
        lastName: info.lastName,
        email: info.email,
        userRole: ['pic'],
        userType: 'External',
        sex: info.sex,
        organization: info.organization,
        sector: this.sectorId,
        subsector: this.subSectorId,
        contactNumber: info.contactNumber,
        termsandconditions: this.termsandconditions
      }
      const resp = await users.createUser(payload)
      if (resp.ok) {
        /* const details = await resp.json()
        const { _id } = details.userData
        await this.upload(payload, _id) */
        this.$store.dispatch('showAlert', {
          message: `We have sent you an email confirmation to ${info.email}. Please check it and use the password that we’ve provided when you login.`,
          type: 'signup',
          callback: this.$router.push({
            path: '/login'
          })
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
      this.isLoading = false
    },
    // upload id image
    async upload (info, id) {
      for (const [key, value] of Object.entries(info.idPicture)) {
        const imageFile = uploadID(key, value)
        await users.uploadID(id, key, imageFile)
      }
      return true
    },
    // get sectors from master data
    // show alert modal when fetching of data failed
    async getSectorData () {
      const data = await this.api('read').list({ count: 10000, advancedQuery: { type: { $eq: 'sector' } } })
      if (data.ok) {
        const sectors = data.entries.filter(item => item.type === 'sector')
        this.sectorValues = sectors.map(sector => sector.value)
        this.sector = sectors
      } else {
        this.$store.dispatch('showAlert', {
          message: (await data.json()).error,
          type: 'error'
        })
      }
    },
    // get subsector data from this.sector
    getSubSectorData () {
      const data = this.sector.filter(sector => sector.value === this.selectedSector)
      const subSectors = data[0].subsector
      this.subSectorValues = subSectors.map(sub => sub.value).sort()
      this.subSector = subSectors
    },
    api () {
      return crud('sector')
    },
    // show confirm dialog when all validations are cleared
    confirm (action) {
      const label = this.capitalize(action)
      this.reason = 'action'
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$store.dispatch('showDialog', {
          title: `${label} an account`,
          message: `Are you sure you want to ${action} this user?`,
          buttonLabel: label,
          buttonColor: 'primary',
          callback: () => {
            this.create()
          }
        })
      }
    }
  }
}
</script>
<style scoped>
.container {
  padding: 2%;
  margin: 0px;
  font-family: Source Sans Pro, sans-serif;
}
.signup {
  font-size: 50px;
  font-weight: 600;
  color: #162b54
}
.padding {
  padding: 10px 20px;
}
.signupCard {
  padding: 20px 30px 20px 30px;
  border-radius: 10px;
  box-shadow: 0px 12px 6px -6px #c3c3c3 !important;
}
.layout {
    width: 100%;
    padding: 0;
    margin-right: auto;
    margin-left: auto;
    margin-top: auto;
    margin-bottom: auto;
  }
.content {
  padding-top: 20px;
}
.here-button {
  color: #51dc58;
}
.italic {
  font-style: italic;
}

</style>
